/**
 * Persistent copy of the player's ftl.dat in the Origin Private File System.
 * The copy is streamed (never fully buffered in memory) and only trusted once a metadata
 * file marks it complete, so an interrupted copy is never used.
 */

const DAT_NAME = 'ftl.dat';
const META_NAME = 'ftl.dat.json';

export interface StoredDatMeta {
  size: number;
  complete: boolean;
  /** 'file' (picked/dropped) or 'server'. */
  source: string;
  savedAt: string;
}

export type ProgressFn = (done: number, total: number) => void;

export function opfsAvailable(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.storage && typeof navigator.storage.getDirectory === 'function';
}

/** Whether streaming writes are possible (FileSystemFileHandle.createWritable). */
export function opfsWritable(): boolean {
  return opfsAvailable() && typeof FileSystemFileHandle !== 'undefined' && 'createWritable' in FileSystemFileHandle.prototype;
}

async function root(): Promise<FileSystemDirectoryHandle> {
  return navigator.storage.getDirectory();
}

async function readMeta(dir: FileSystemDirectoryHandle): Promise<StoredDatMeta | null> {
  try {
    const f = await (await dir.getFileHandle(META_NAME)).getFile();
    return JSON.parse(await f.text()) as StoredDatMeta;
  } catch {
    return null;
  }
}

/** The stored ftl.dat as a File, or null when there is no complete copy. */
export async function getStoredDat(): Promise<File | null> {
  if (!opfsAvailable()) return null;
  try {
    const dir = await root();
    const meta = await readMeta(dir);
    if (!meta?.complete) return null;
    const file = await (await dir.getFileHandle(DAT_NAME)).getFile();
    return file.size === meta.size ? file : null;
  } catch {
    return null;
  }
}

/** Info about the stored copy (for a settings screen). */
export async function getStoredDatInfo(): Promise<StoredDatMeta | null> {
  if (!opfsAvailable()) return null;
  try {
    return await readMeta(await root());
  } catch {
    return null;
  }
}

/** Asks the browser not to evict our storage. Returns whether it is persisted. */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/**
 * Streams `data` into OPFS as the stored ftl.dat. `size` is the expected byte length
 * (used for progress and validation). Throws if OPFS writes are unsupported or the stream
 * ends early.
 */
export async function storeDat(
  data: ReadableStream<Uint8Array>,
  size: number,
  source: string,
  onProgress?: ProgressFn,
  signal?: AbortSignal,
): Promise<File> {
  if (!opfsWritable()) throw new Error('OPFS streaming writes are not supported by this browser');
  const dir = await root();
  // Invalidate any previous copy first so a crash mid-copy leaves no "complete" marker.
  await dir.removeEntry(META_NAME).catch(() => undefined);
  const handle = await dir.getFileHandle(DAT_NAME, { create: true });
  const writable = await handle.createWritable({ keepExistingData: false });
  let done = 0;
  const counter = new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, ctl) {
      done += chunk.byteLength;
      onProgress?.(done, size);
      ctl.enqueue(chunk);
    },
  });
  await data.pipeThrough(counter, { signal }).pipeTo(writable as unknown as WritableStream<Uint8Array>, { signal });
  if (done !== size) throw new Error(`copy incomplete: ${done} of ${size} bytes`);
  const meta: StoredDatMeta = { size, complete: true, source, savedAt: new Date().toISOString() };
  const mw = await (await dir.getFileHandle(META_NAME, { create: true })).createWritable();
  await mw.write(JSON.stringify(meta));
  await mw.close();
  return (await dir.getFileHandle(DAT_NAME)).getFile();
}

/** Deletes the stored copy. */
export async function forgetStoredDat(): Promise<void> {
  if (!opfsAvailable()) return;
  const dir = await root();
  await dir.removeEntry(META_NAME).catch(() => undefined);
  await dir.removeEntry(DAT_NAME).catch(() => undefined);
}
